import React, { useEffect, useRef, useState, ReactNode, KeyboardEvent } from 'react';
import { X, Eye, EyeOff, Save, Download, Copy, Check, Edit2, Trash2, QrCode, Clock, Plus, Minus } from 'lucide-react';
import QRShareModal from './QRShareModal';
import timelineService from '../../features/timeline/services/timelineService';
import { useTime } from '../context/TimeContext';
import { useTheme, ThemeOption } from '../context/ThemeContext';
import { useAuth } from '../../features/auth/context/AuthContext';
import { formatTo12Hour } from '@thekwallace/backtime-shared';
import packageJson from '../../../package.json';

interface SettingsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isPublic?: boolean;
  onTogglePublic?: () => void;
  publicCode?: string | null;
  onPublicCodeChange?: (code: string) => void;
  onSetDefaults?: () => void;
  onLoadDefaults?: () => void;
  isOwner?: boolean;
  timelineData?: Record<string, unknown> | null;
  timelineId?: string;
  onDeleteTimeline?: () => void;
  canDelete?: boolean;
  // Timeline name editing props
  timelineName?: string;
  onTimelineNameChange?: (name: string) => void;
  // Occurrence selection props
  isAutoAdvanceEnabled?: boolean;
  onAutoAdvanceChange?: (enabled: boolean) => void;
  autoAdvanceBuffer?: number; // in seconds
  onAutoAdvanceBufferChange?: (buffer: number) => void;
  // Time sync props
  timeOffset?: number; // in seconds
  onTimeOffsetChange?: (offset: number) => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  isOpen,
  onClose,
  isPublic = false,
  onTogglePublic,
  publicCode = null,
  onPublicCodeChange,
  onSetDefaults,
  onLoadDefaults,
  isOwner = false,
  timelineData = null,
  timelineId = '',
  onDeleteTimeline,
  canDelete = true,
  // Timeline name editing props
  timelineName,
  onTimelineNameChange,
  // Occurrence selection props
  isAutoAdvanceEnabled,
  onAutoAdvanceChange,
  autoAdvanceBuffer = 0, // in seconds
  onAutoAdvanceBufferChange,
  // Time sync props
  timeOffset = 0, // in seconds
  onTimeOffsetChange,
}) => {
  const { getTimeWithOffset } = useTime();
  const currentTimeSeconds = getTimeWithOffset(timeOffset);
  const { theme, setTheme, systemTheme } = useTheme();
  const { currentUser } = useAuth();
  const isAuthenticated = !!currentUser;
  const [showQRModal, setShowQRModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editedCode, setEditedCode] = useState(publicCode || '');
  const [codeError, setCodeError] = useState('');
  // Timeline name editing state
  const [editedName, setEditedName] = useState(timelineName || '');
  const menuRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Sync edited name when prop changes
  useEffect(() => {
    setEditedName(timelineName || '');
  }, [timelineName]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleCopyLink = async () => {
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/t/${publicCode}`;

    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSetDefaults = () => {
    onSetDefaults?.();
    onClose();
  };

  const handleLoadDefaults = () => {
    onLoadDefaults?.();
    onClose();
  };

  const handleTogglePublic = () => {
    if (onTogglePublic) {
      onTogglePublic();
    }
  };

  // Handle timeline name save
  const handleNameSave = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== timelineName && onTimelineNameChange) {
      onTimelineNameChange(trimmedName);
    } else if (!trimmedName) {
      setEditedName(timelineName || '');
    }
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditedName(timelineName || '');
      (e.target as HTMLInputElement).blur();
    }
  };

  useEffect(() => {
    setEditedCode(publicCode || '');
    setCodeError('');
  }, [publicCode]);

  const handleEditCode = () => {
    setIsEditingCode(true);
    setEditedCode(publicCode || '');
    setCodeError('');
  };

  const handleCancelEditCode = () => {
    setIsEditingCode(false);
    setEditedCode(publicCode || '');
    setCodeError('');
  };

  const handleSaveCode = async () => {
    const trimmedCode = editedCode.trim().toUpperCase();

    if (!/^[A-Z0-9]{6}$/.test(trimmedCode)) {
      setCodeError('Code must be exactly 6 alphanumeric characters');
      return;
    }

    try {
      const updatedTimeline = await timelineService.updatePublicCode(timelineId, trimmedCode);
      setIsEditingCode(false);
      setCodeError('');
      if (onPublicCodeChange && updatedTimeline.publicCode) {
        onPublicCodeChange(updatedTimeline.publicCode);
      }
    } catch (error) {
      const apiError = error as { response?: { data?: { error?: string } } };
      if (apiError.response?.data?.error) {
        setCodeError(apiError.response.data.error);
      } else {
        setCodeError('Failed to update code. Please try again.');
      }
    }
  };

  const handleDeleteTimeline = () => {
    if (!canDelete) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this timeline? This action cannot be undone.'
    );

    if (confirmed && onDeleteTimeline) {
      onDeleteTimeline();
      onClose();
    }
  };

  // Reusable section header component
  const SectionHeader: React.FC<{ children: ReactNode }> = ({ children }) => (
    <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
      {children}
    </h2>
  );

  // Reusable subsection header component
  const SubsectionHeader: React.FC<{ children: ReactNode }> = ({ children }) => (
    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
      {children}
    </h3>
  );

  // Reusable footer text component
  const FooterText: React.FC<{ children: ReactNode }> = ({ children }) => (
    <p className="text-xs text-gray-500 leading-relaxed mt-2">
      {children}
    </p>
  );

  // Reusable action button component
  interface ActionButtonProps {
    onClick: () => void;
    icon?: React.ComponentType<{ className?: string }>;
    children: ReactNode;
    variant?: 'default' | 'primary' | 'danger';
    disabled?: boolean;
    className?: string;
  }

  const ActionButton: React.FC<ActionButtonProps> = ({ onClick, icon: Icon, children, variant = 'default', disabled = false, className = '' }) => {
    const variants = {
      default: 'bg-gray-200/50 dark:bg-gray-700/50 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600',
      primary: 'bg-blue-600/10 hover:bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-500/30',
      danger: 'bg-red-600/10 hover:bg-red-600/20 text-red-600 dark:text-red-400 border border-red-500/30',
      disabled: 'bg-gray-100/50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-700 cursor-not-allowed',
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-150 ${
          disabled ? variants.disabled : variants[variant]
        } ${className}`}
      >
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
        <span className="text-sm font-medium">{children}</span>
      </button>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 z-[1100] ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Slide-out menu */}
      <div
        ref={menuRef}
        className={`fixed left-0 top-0 h-full w-80 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out z-[1200] ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">Settings</h2>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex-1 overflow-y-auto">
            {/* ==================== TIMELINE SECTION ==================== */}
            <div className="px-5 py-5">
              <SectionHeader>Timeline</SectionHeader>

              {/* Non-owner info banner */}
              {!isOwner && (
                <div className="flex items-start gap-3 px-4 py-3 mb-5 bg-blue-600/10 border border-blue-500/30 rounded-lg">
                  <Eye className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-300">
                    You're viewing a shared timeline. Only the owner can modify settings.
                  </p>
                </div>
              )}

              {/* Timeline Name Subsection - viewable by all, editable by owner */}
              <div className="space-y-2 mb-5">
                <SubsectionHeader>Name</SubsectionHeader>
                {isOwner && onTimelineNameChange ? (
                  <>
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={handleNameKeyDown}
                      className="w-full bg-gray-100 dark:bg-gray-800 px-4 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Enter timeline name"
                    />
                    <FooterText>
                      Give your timeline a memorable name.
                    </FooterText>
                  </>
                ) : (
                  <div className="px-4 py-2.5 bg-gray-100/30 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-800">
                    <span className="text-sm text-gray-900 dark:text-white">{timelineName || 'Untitled Timeline'}</span>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-800 my-4"></div>

              {/* Sharing Subsection - Owner only for toggle, visible by all for public code */}
              {isOwner && onTogglePublic ? (
                <div className="space-y-3 mb-5">
                  <SubsectionHeader>Sharing</SubsectionHeader>

                  {/* Public/Private Toggle - Owner only */}
                  <button
                    onClick={handleTogglePublic}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-150 ${
                      isPublic
                        ? 'bg-emerald-600/10 border border-emerald-500/30'
                        : 'bg-gray-100/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {isPublic ? (
                        <Eye className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      )}
                      <span className={`text-sm font-medium ${isPublic ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>
                    <div className={`relative w-10 h-5 rounded-full transition-colors ${
                      isPublic ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-700'
                    }`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        isPublic ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>

                  {isPublic && publicCode && (
                    <div className="space-y-3 mt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Share Code</span>
                      </div>

                      {isEditingCode ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editedCode}
                            onChange={(e) => setEditedCode(e.target.value.toUpperCase())}
                            maxLength={6}
                            className="w-full bg-gray-100 dark:bg-gray-800 px-4 py-2.5 rounded-lg font-mono text-base text-center text-gray-900 dark:text-white border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            placeholder="ABC123"
                          />
                          {codeError && (
                            <p className="text-xs text-red-500 dark:text-red-400">{codeError}</p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveCode}
                              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEditCode}
                              className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 dark:bg-gray-800 px-4 py-2.5 rounded-lg font-mono text-base text-center text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700">
                            {publicCode}
                          </div>
                          <button
                            onClick={handleEditCode}
                            className="p-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
                            title="Edit code"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCopyLink}
                            className="p-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
                            title="Copy public link"
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => setShowQRModal(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors text-sm"
                      >
                        <QrCode className="w-4 h-4" />
                        <span>Show QR Code</span>
                      </button>

                      <FooterText>
                        Share this code or scan the QR to let others view your timeline.
                      </FooterText>
                    </div>
                  )}

                  {!isPublic && (
                    <FooterText>
                      Enable sharing to let others view your timeline.
                    </FooterText>
                  )}
                </div>
              ) : isPublic && publicCode && (
                /* Non-owner view of public code - read-only */
                <div className="space-y-3 mb-5">
                  <SubsectionHeader>Share Code</SubsectionHeader>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100/30 dark:bg-gray-800/30 px-4 py-2.5 rounded-lg font-mono text-base text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-800">
                      {publicCode}
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="p-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
                      title="Copy public link"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowQRModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors text-sm"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>Show QR Code</span>
                  </button>
                </div>
              )}

              {/* Occurrence Selection Subsection */}
              {isOwner && onAutoAdvanceChange && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-800 my-4"></div>
                  <div className="space-y-3 mb-5">
                    <SubsectionHeader>Occurrence Selection</SubsectionHeader>

                    {/* Auto-Advance Toggle */}
                    <button
                      onClick={() => onAutoAdvanceChange(!isAutoAdvanceEnabled)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-150 ${
                        isAutoAdvanceEnabled
                          ? 'bg-blue-600/10 border border-blue-500/30'
                          : 'bg-gray-100/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${isAutoAdvanceEnabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}>
                          Auto-Advance
                        </span>
                      </div>
                      <div className={`relative w-10 h-5 rounded-full transition-colors ${
                        isAutoAdvanceEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'
                      }`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          isAutoAdvanceEnabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>

                    {/* Buffer Input */}
                    {isAutoAdvanceEnabled && onAutoAdvanceBufferChange && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Buffer (minutes)</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          max="60"
                          value={Math.round(autoAdvanceBuffer / 60)}
                          onChange={(e) => {
                            const minutes = Math.max(0, Math.min(60, parseInt(e.target.value) || 0));
                            onAutoAdvanceBufferChange(minutes * 60);
                          }}
                          className="w-full bg-gray-100 dark:bg-gray-800 px-4 py-2.5 rounded-lg text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        />
                      </div>
                    )}

                    <FooterText>
                      {isAutoAdvanceEnabled
                        ? 'Automatically selects the next occurrence based on the schedule. Buffer adds time before switching.'
                        : 'When disabled, you must manually select which occurrence to display.'}
                    </FooterText>
                  </div>
                </>
              )}

              {/* Time Sync Subsection */}
              <div className="border-t border-gray-200 dark:border-gray-800 my-4"></div>
              <div className="space-y-3 mb-5">
                <SubsectionHeader>Time Sync</SubsectionHeader>

                {/* Current Time Display */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg border border-gray-300 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Current Time</span>
                  </div>
                  <span className="text-sm font-mono text-gray-900 dark:text-white">
                    {formatTo12Hour(currentTimeSeconds, true)}
                  </span>
                </div>

                {/* Time Offset Display and Controls */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Time Offset</span>
                    <span className={`text-xs font-mono ${timeOffset === 0 ? 'text-gray-500' : timeOffset > 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {timeOffset >= 0 ? '+' : ''}{timeOffset}s
                    </span>
                  </div>

                  {isOwner && onTimeOffsetChange && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onTimeOffsetChange(timeOffset - 1)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                        <span className="text-sm">1s</span>
                      </button>
                      <button
                        onClick={() => onTimeOffsetChange(0)}
                        disabled={timeOffset === 0}
                        className={`px-4 py-2 rounded-lg border transition-colors text-sm ${
                          timeOffset === 0
                            ? 'bg-gray-100/50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-800 cursor-not-allowed'
                            : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                        }`}
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => onTimeOffsetChange(timeOffset + 1)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm">1s</span>
                      </button>
                    </div>
                  )}
                </div>

                {isOwner && (
                  <FooterText>
                    Adjust if your display clock differs from the reference clock. Positive values move time forward.
                  </FooterText>
                )}
              </div>

              {/* Defaults Subsection - authenticated users only */}
              {isAuthenticated && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-800 my-4"></div>
                  <div className="space-y-2 mb-5">
                    <SubsectionHeader>Defaults</SubsectionHeader>

                    <ActionButton onClick={handleSetDefaults} icon={Save}>
                      Save as Defaults
                    </ActionButton>

                    <ActionButton onClick={handleLoadDefaults} icon={Download}>
                      Load Defaults
                    </ActionButton>

                    <FooterText>
                      Save the current configuration as your default, or restore previously saved defaults.
                    </FooterText>
                  </div>
                </>
              )}

              {/* Danger Zone Subsection */}
              {isOwner && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-800 my-4"></div>
                  <div className="space-y-2">
                    <SubsectionHeader>Danger Zone</SubsectionHeader>

                    <ActionButton
                      onClick={handleDeleteTimeline}
                      icon={Trash2}
                      variant={canDelete ? 'danger' : 'default'}
                      disabled={!canDelete}
                    >
                      Delete Timeline
                    </ActionButton>

                    {!canDelete && (
                      <FooterText>
                        You cannot delete your only timeline. Create another timeline first.
                      </FooterText>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ==================== GENERAL SECTION ==================== */}
            <div className="px-5 py-5 border-t border-gray-200 dark:border-gray-800">
              <SectionHeader>General</SectionHeader>

              {/* Appearance Subsection */}
              <div className="space-y-3 mb-5">
                <SubsectionHeader>Appearance</SubsectionHeader>

                {/* Theme Selector */}
                <div className="flex gap-2">
                  {([
                    { value: 'light' as ThemeOption, label: 'Light' },
                    { value: 'dark' as ThemeOption, label: 'Dark' },
                    { value: 'system' as ThemeOption, label: 'System' },
                  ] as const).map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setTheme(value)}
                      className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                        theme === value
                          ? 'bg-blue-600 text-white border border-blue-500'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {theme === 'system' && (
                  <p className="text-xs text-gray-500">
                    Currently using <span className="text-gray-600 dark:text-gray-400">{systemTheme}</span> based on your system settings.
                  </p>
                )}

                <FooterText>
                  Choose your preferred color scheme.
                </FooterText>
              </div>

              {/* About Subsection */}
              <div className="space-y-2">
                <SubsectionHeader>About</SubsectionHeader>
                <div className="px-4 py-3 bg-gray-100/30 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-800">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    backtime.live
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-600 mt-1">
                    Version {packageJson.version}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      <QRShareModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        data={timelineData || {}}
        isPublic={isPublic}
        publicCode={publicCode ?? undefined}
      />
    </>
  );
};

export default SettingsMenu;
